/**
 * Formatacao de data para APRESENTACAO, em pt-BR (spec 4.4).
 *
 * A aritmetica mora em `@cleartask/shared`; aqui so se produz texto. A divisao
 * importa porque o fuso de referencia e o de `users.timezone`, e nao o do
 * dispositivo: abrir o app num notebook configurado em UTC nao pode mudar o
 * que conta como "hoje".
 */

import { dayDifferenceInZone, partsInZone } from '@cleartask/shared';

const MESES_ABREV = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

const DIAS_SEMANA = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
];

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Hora de parede no formato 24h — 'HH:MM'. */
export function formatTime(epochMs: number, timeZone: string): string {
  const p = partsInZone(timeZone, epochMs);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** Data curta — '21/jul', ou '21/jul/2027' quando cai em outro ano. */
export function formatShortDate(epochMs: number, now: number, timeZone: string): string {
  const p = partsInZone(timeZone, epochMs);
  const anoAtual = partsInZone(timeZone, now).year;

  const base = `${pad(p.day)}/${MESES_ABREV[p.month - 1] ?? '?'}`;
  return p.year === anoAtual ? base : `${base}/${p.year}`;
}

/**
 * Rotulo do prazo de uma tarefa, como aparece no card.
 *
 * Nos dias vizinhos usa palavra em vez de numero ("ontem 14:00"): a data
 * absoluta obriga o leitor a calcular a distancia, que e justamente a
 * informacao que ele quer.
 *
 * @param allDay tarefa sem hora marcada — mostrar '00:00' seria inventar uma
 *        precisao que o usuario nao forneceu.
 */
export function formatDueLabel(
  dueAt: number,
  allDay: boolean,
  now: number,
  timeZone: string,
): string {
  const diff = dayDifferenceInZone(timeZone, now, dueAt);
  const hora = allDay ? '' : ` ${formatTime(dueAt, timeZone)}`;

  if (diff === 0) return allDay ? 'hoje' : formatTime(dueAt, timeZone);
  if (diff === -1) return `ontem${hora}`;
  if (diff === 1) return `amanhã${hora}`;

  // Dentro da proxima semana, o dia da semana localiza melhor que a data.
  if (diff > 1 && diff <= 6) {
    const nome = DIAS_SEMANA[partsInZone(timeZone, dueAt).weekday] ?? '';
    return `${nome}${hora}`;
  }

  return `${formatShortDate(dueAt, now, timeZone)}${hora}`;
}

/** Cabecalho de agrupamento — 'quinta, 30/jul'. */
export function formatGroupDate(epochMs: number, now: number, timeZone: string): string {
  const p = partsInZone(timeZone, epochMs);
  const nome = DIAS_SEMANA[p.weekday] ?? '';
  return `${nome}, ${formatShortDate(epochMs, now, timeZone)}`;
}

/**
 * Vencida = prazo no passado.
 *
 * Comparacao direta de instantes, sem fuso: "ja passou" e a mesma verdade em
 * qualquer lugar do mundo. O fuso so importa para decidir a que DIA a tarefa
 * pertence, que e outra pergunta.
 */
export function isOverdue(dueAt: number | null, now: number): boolean {
  return dueAt !== null && dueAt < now;
}
